import { isSafari } from "../utils/detectDevice"

type PdfjsLib = typeof import("pdfjs-dist")
type PdfWorker = InstanceType<PdfjsLib["PDFWorker"]>

let libPromise: Promise<PdfjsLib> | null = null
let worker: PdfWorker | null = null

const workerSrc = (version: string) =>
  isSafari || window.Cypress
    ? // Safari needs the locally hosted worker, which carries our polyfills.
      `${window.location.origin}/pdf.worker.min.mjs`
    : `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.mjs`

/**
 * Imports pdfjs-dist once and configures the worker source once.
 *
 * Every caller shares this promise, so the ~1 MB library is only fetched,
 * compiled and configured a single time per page load.
 */
export const loadPdfjs = (): Promise<PdfjsLib> => {
  if (!libPromise) {
    libPromise = import("pdfjs-dist").then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = workerSrc(lib.version)
      return lib
    })
  }
  return libPromise
}

/**
 * One PDF.js worker for the whole app.
 *
 * Without this, `getDocument()` spawns a fresh worker per document, and each
 * spawn re-downloads and re-compiles the pdf.js worker bundle. Opening a chat
 * with several PDF attachments used to start several workers at once.
 *
 * Documents opened with this worker must NOT terminate it: `doc.destroy()`
 * only tears down workers that pdf.js created itself, so passing a shared
 * worker keeps it alive for the next document.
 */
export const getSharedPdfWorker = async (): Promise<PdfWorker> => {
  const lib = await loadPdfjs()

  // `destroyed` turns true if anything ever tears the worker down, so the next
  // caller gets a working one instead of a dead handle.
  if (!worker || worker.destroyed) {
    worker = new lib.PDFWorker()
  }

  return worker
}

/** Test hook: drops the cached library and worker. */
export const resetPdfjsForTests = () => {
  libPromise = null
  worker = null
}
